import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useMemo } from 'react';
import { reportsStore } from '../../store/reportsStore';
import { IReportRow, REPORTS_BODY_KEY, REPORT_TYPE } from '../../types/reports';
import ReportTableColumnTitles from './components/ReportTableColumnTitles/ReportTableColumnTitles';
import ReportTableField from './components/ReportTableField/ReportTableField';
import { localeStore } from 'src/mobx/localesStore';
import { calcAverageCheckValue, getReportTotal, sortReport, sumTotal } from 'src/view/screens/ReportsScreen/helpers/reports';
import { IResponseReportBody } from '~/types/reports';
import { ICustomTooltipProps } from '~/view/components/Controls/CustomTooltip/CustomTooltip';
import './style.scss';

interface IProps {
  noSorting?: boolean;
  actualReport?: IReportRow[];
  comparativeReport?: IReportRow[];
  reportColumnName: string; // e.g. item, agent, month
  columnTooltips?: { [key in REPORTS_BODY_KEY]?: Partial<ICustomTooltipProps> };
  disabledRevenue?: boolean;
  reportType?: REPORT_TYPE;
  className?: string;
}

const ReportContent = observer((props: IProps) => {
  const { noSorting, actualReport, comparativeReport, reportColumnName, columnTooltips, disabledRevenue, reportType, className = '' } = props;
  const [sortField, sortDir] = [reportsStore.filters.sortField, reportsStore.filters.sortDir];

  // temporary excluded fields from reports
  const excludedFields: string[] = [
    REPORTS_BODY_KEY.REVENUE,
    REPORTS_BODY_KEY.AVERAGE_CHECK,
    REPORTS_BODY_KEY.PICKUP_REVENUE,
    REPORTS_BODY_KEY.DELIVERY_REVENUE,
    REPORTS_BODY_KEY.DINEIN_REVENUE,
  ];

  const reportFields = () => {
    if (!actualReportTotal) return [];
    if (disabledRevenue) return Object.entries(actualReportTotal).filter(([key]) => !excludedFields.includes(key));
    return Object.entries(actualReportTotal);
  };
  const reportBodyFields = (row: IReportRow) => {
    if (disabledRevenue) return Object.entries(row.body).filter(([key]) => !excludedFields.includes(key) && key !== 'item');
    return Object.entries(row.body);
  };
  const comparedTableValue = (key: REPORTS_BODY_KEY): number | undefined => {
    if (!sortedComparativeReport) return undefined;
    if (key === REPORTS_BODY_KEY.AVERAGE_CHECK) return calcAverageCheckValue(sortedComparativeReport);
    return sumTotal(sortedComparativeReport, key);
  };

  const sortedActualReport: IReportRow[] | null = actualReport ? sortReport(actualReport, sortField, sortDir, noSorting) : null;
  const sortedComparativeReport: IReportRow[] | null = comparativeReport ? sortReport(comparativeReport, sortField, sortDir, noSorting) : null;

  const actualReportTotal: IResponseReportBody | null = useMemo(
    () => (actualReport && actualReport.length ? getReportTotal(actualReport, reportType) : null),
    [actualReport]
  );

  const renderBody = useMemo(() => {
    return sortedActualReport?.map((row, index) => {
      const bodyFields = reportBodyFields(row);
      return (
        <div key={index} className="d-flex flex-row reports-table-string py-3">
          <ReportTableField value={row.name} />
          {bodyFields.map(([key, value]) => {
            return (
              <ReportTableField
                key={`${key}-table-rows`}
                value={value}
                comparedValue={sortedComparativeReport ? sortedComparativeReport[index].body[key as REPORTS_BODY_KEY] : undefined}
              />
            );
          })}
        </div>
      );
    });
  }, [sortedActualReport, sortedComparativeReport]);

  return (
    <div className={classNames(`reports-table scrollable-block__scroll_small mr-4 ${className}`)}>
      <ReportTableColumnTitles
        reportColumnName={reportColumnName}
        columnTooltips={columnTooltips}
        disabledRevenue={disabledRevenue}
        reportType={reportType}
      />

      {sortedActualReport && actualReportTotal ? (
        <>
          <div className="d-flex flex-row reports-table-string border-white pr-1 py-3">
            <ReportTableField value={localeStore.t('txt_total')} textClassName="family-semibold" />
            {reportFields().map(([key, value]) => (
              <ReportTableField
                key={`${key}-table-string`}
                value={value}
                comparedValue={comparedTableValue(key as REPORTS_BODY_KEY)}
                textClassName="family-semibold"
              />
            ))}
          </div>

          <div className="reports-table-rows scrollable-block__scroll_small mb-3 d-flex flex-column flex-grow-1">{renderBody}</div>
        </>
      ) : null}
    </div>
  );
});

export default ReportContent;
